import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Container,
  Collapse,
  TextField,
  InputAdornment,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { ChevronDown, ChevronUp, Search, ExternalLink, ChevronRight } from "lucide-react";
import bots from "../const/bots.json";
import mainLogo from "../assets/mainLogo.png";
import character0 from "../assets/characters/0.png";
import character1 from "../assets/characters/1.png";
import character2 from "../assets/characters/2.png";
import character3 from "../assets/characters/3.png";
import character4 from "../assets/characters/4.png";
import character5 from "../assets/characters/5.png";
import character6 from "../assets/characters/6.png";
import character7 from "../assets/characters/7.png";
import character8 from "../assets/characters/8.png";
import character9 from "../assets/characters/9.png";
import character10 from "../assets/characters/10.png";
import character11 from "../assets/characters/11.png";
import character12 from "../assets/characters/12.png";
import character13 from "../assets/characters/13.png";
import character14 from "../assets/characters/14.png";
import character15 from "../assets/characters/15.png";
import character16 from "../assets/characters/16.png";
import character17 from "../assets/characters/17.png";
import character18 from "../assets/characters/18.png";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import generalApi from "../api/generalApi";
import profileApi from "../api/profileApi";
import { useLimits } from '../context/LimitsContext';
import SubscriptionModal from "./common/SubscriptionModal";
import { getErrorMessage } from "../utils/translations";

// Map of available character images
const characterImages = {
  0: character0,
  1: character1,
  2: character2,
  3: character3,
  4: character4,
  5: character5,
  6: character6,
  7: character7,
  8: character8,
  9: character9,
  10: character10,
  11: character11,
  12: character12,
  13: character13,
  14: character14,
  15: character15,
  16: character16,
  17: character17,
  18: character18,
};

const TypeWriter = ({ text, delay = 50, onComplete = () => {} }) => {
  const [currentText, setCurrentText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setCurrentText(prevText => prevText + text[currentIndex]);
        setCurrentIndex(prevIndex => prevIndex + 1);
      }, delay);

      return () => clearTimeout(timeout);
    } else {
      onComplete();
    }
  }, [currentIndex, delay, text, onComplete]);

  return <span>{currentText}</span>;
};

const TaskCard = ({ task, backgroundColor, searchTerms = [], isExpanded, bot, onClick }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionUrl, setSubscriptionUrl] = useState('');
  const { limits } = useLimits();
  const isImagesAllowed = limits?.imagesAllowed;
  const isImageBot = bot.botId === 0; // Mich-AI-Langelo bot
  
  const handleTaskClick = async (e) => {
    e.stopPropagation(); // Prevent the bot card from expanding
    
    if (isLoading) return; // Prevent multiple clicks
    
    // For image generation bot, redirect to /images if allowed
    if (isImageBot) {
      if (isImagesAllowed) {
        navigate('/images');
      } else {
        try {
          const url = await generalApi.getWordPressLoginUrl();
          if (!url) {
            throw new Error('No URL returned from server');
          }
          setSubscriptionUrl(url);
          setShowSubscriptionModal(true);
        } catch (error) {
          console.error('Error loading subscription page:', error);
          toast.error('Unable to load subscription page. Please try again later.');
        }
      }
      return;
    }
    
    try {
      setIsLoading(true);
      // Create a new thread with bot and task information
      const response = await generalApi.chat.createThread({
        botId: bot.botId,
        taskTitle: task.taskTitle,
        title: task.taskTitle
      });

      if (response?.threadId) {
        // Navigate to chat with the new thread
        navigate(`/chat/${bot.botId}`);
      }
    } catch (error) {
      console.error('Failed to create thread:', error);
      toast.error('Failed to create new chat');
      setIsLoading(false);
    }
  };

  const highlightText = (text) => {
    if (!searchTerms.length) return text;
    let highlightedText = text;
    searchTerms.forEach(term => {
      const regex = new RegExp(`(${term})`, 'gi');
      highlightedText = highlightedText.replace(regex, '<mark style="background-color: yellow; color: black; padding: 0 2px; border-radius: 2px;">$1</mark>');
    });
    return <span dangerouslySetInnerHTML={{ __html: highlightedText }} />;
  };

  // Show keywords either when there are matching search terms or when the parent card is expanded
  const shouldShowKeywords = searchTerms.length > 0 || isExpanded;
  const keywordsToShow = searchTerms.length > 0 
    ? task.keywords.filter(keyword => 
        searchTerms.some(term => 
          keyword.toLowerCase().includes(term.toLowerCase())
        )
      )
    : task.keywords;

  return (
    <>
      <Box
        onClick={handleTaskClick}
        sx={{
          backgroundColor: `${backgroundColor}05`,
          borderRadius: '12px',
          border: `1px solid ${backgroundColor}30`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          minWidth: { xs: '100%', md: '300px' },
          width: { xs: '100%', md: '300px' },
          flex: '0 0 auto',
          position: 'relative',
          background: `linear-gradient(135deg, 
            ${backgroundColor}10 0%, 
            ${backgroundColor}05 50%,
            ${backgroundColor}15 100%)`,
          boxShadow: `0 4px 20px -8px ${backgroundColor}30`,
          cursor: isImageBot && !isImagesAllowed ? 'not-allowed' : (isLoading ? 'wait' : 'pointer'),
          opacity: isImageBot && !isImagesAllowed ? 0.7 : (isLoading ? 0.7 : 1),
          filter: isImageBot && !isImagesAllowed ? 'grayscale(50%)' : 'none',
          "&:before": {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: '12px',
            background: `linear-gradient(135deg, 
              ${backgroundColor}20 0%, 
              ${backgroundColor}10 25%,
              ${backgroundColor}25 50%,
              ${backgroundColor}10 75%,
              ${backgroundColor}20 100%)`,
            opacity: 0,
            transition: 'opacity 0.3s ease-in-out',
          },
          "&:hover": {
            transform: (isImageBot && !isImagesAllowed) || isLoading ? 'none' : 'translateY(-2px)',
            boxShadow: (isImageBot && !isImagesAllowed) || isLoading ? `0 4px 20px -8px ${backgroundColor}30` : `0 8px 28px -6px ${backgroundColor}40`,
            "&:before": {
              opacity: (isImageBot && !isImagesAllowed) || isLoading ? 0 : 1,
            }
          }
        }}
      >
        <Box
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, pt: 1 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                backgroundColor: `${backgroundColor}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2.5,
                flexShrink: 0,
                mt: 1,
                position: 'relative',
              }}
            >
              {isLoading ? (
                <CircularProgress
                  size={24}
                  sx={{
                    color: backgroundColor,
                    position: 'absolute',
                  }}
                />
              ) : (
                <i 
                  className={task.taskIcon}
                  style={{ 
                    color: backgroundColor, 
                    fontSize: '1.25rem',
                  }} 
                />
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 600, 
                  lineHeight: 1.3,
                  mt: 1.25,
                }}
              >
                {highlightText(task.taskTitle)}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: backgroundColor,
                  opacity: 0.8,
                  display: 'block',
                  mt: 0.5,
                  fontSize: '0.75rem',
                }}
              >
                {task.aiCreditsToCharge} Credits
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mb: shouldShowKeywords ? 2 : 0 }}>
            {highlightText(task.taskDescription)}
          </Typography>
          {shouldShowKeywords && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {keywordsToShow.map((keyword, index) => (
                <Typography
                  key={index}
                  variant="caption"
                  sx={{
                    color: backgroundColor,
                    backgroundColor: `${backgroundColor}15`,
                    padding: '4px 8px',
                    borderRadius: '6px',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                  }}
                >
                  {highlightText(keyword)}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
        <Backdrop
          open={isLoading}
          sx={{
            position: 'absolute',
            borderRadius: '12px',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            zIndex: 2,
          }}
        >
          <CircularProgress sx={{ color: backgroundColor }} />
        </Backdrop>
      </Box>

      <SubscriptionModal
        open={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        subscriptionUrl={subscriptionUrl}
      />
    </>
  );
};

const BotCard = ({ bot, isExpanded, isCollapsed, isSecondInRow, onToggle, searchTerms }) => {
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [showContent, setShowContent] = React.useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = React.useState(false);
  const [subscriptionUrl, setSubscriptionUrl] = React.useState('');
  const navigate = useNavigate();
  const { limits } = useLimits();
  const isImagesAllowed = limits?.imagesAllowed;
  const isImageBot = bot.botId === 0; // Mich-AI-Langelo bot

  React.useEffect(() => {
    if (isExpanded) {
      setIsAnimating(true);
      const horizontalTimer = setTimeout(() => {
        setShowContent(true);
        setIsAnimating(false);
      }, 300);
      return () => clearTimeout(horizontalTimer);
    } else {
      setShowContent(false);
      const collapseTimer = setTimeout(() => {
        setIsAnimating(false);
      }, 300);
      return () => clearTimeout(collapseTimer);
    }
  }, [isExpanded]);

  const handleChatClick = async (e) => {
    e.stopPropagation();
    if (isImageBot) {
      if (isImagesAllowed) {
        navigate('/images');
      } else {
        try {
          const url = await generalApi.getWordPressLoginUrl();
          setSubscriptionUrl(url);
          setShowSubscriptionModal(true);
        } catch (error) {
          toast.error('Error loading subscription page. Please try again.');
        }
      }
    } else {
      navigate(`/chat/${bot.botId}`);
    }
  };

  const handleUpgradeClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const url = await profileApi.getWordPressLoginUrl();
      if (!url) {
        throw new Error('No URL returned from server');
      }
      setSubscriptionUrl(url);
      setShowSubscriptionModal(true);
    } catch (error) {
      console.error('Error loading subscription page:', error);
      const errorMessage = getErrorMessage("ERROR_LOADING_BILLING_CONTACT_SUPPORT");
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          boxShadow: isExpanded 
            ? `0 8px 32px -8px ${bot.botColor}50` 
            : isCollapsed 
              ? `0 8px 28px -8px ${bot.botColor}40`
              : "none",
          border: `1px solid ${bot.botColor}40`,
          background: isCollapsed 
            ? `linear-gradient(135deg, 
                ${bot.botColor}08 0%, 
                ${bot.botColor}02 100%)`
            : isExpanded 
              ? `linear-gradient(135deg, 
                  ${bot.botColor}15 0%, 
                  ${bot.botColor}05 25%,
                  ${bot.botColor}10 50%,
                  ${bot.botColor}05 75%,
                  ${bot.botColor}15 100%)`
              : `linear-gradient(135deg, 
                  ${bot.botColor}08 0%, 
                  ${bot.botColor}02 100%)`,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          borderRadius: '16px',
          overflow: 'hidden',
          cursor: isImageBot && !isImagesAllowed ? 'not-allowed' : 'pointer',
          transform: isExpanded ? 'scale(1.01)' : 'scale(1)',
          position: 'relative',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: '16px',
            background: isExpanded 
              ? `linear-gradient(135deg, 
                  ${bot.botColor}25 0%, 
                  ${bot.botColor}10 25%,
                  ${bot.botColor}20 50%,
                  ${bot.botColor}10 75%,
                  ${bot.botColor}25 100%)`
              : `linear-gradient(135deg, 
                  ${bot.botColor}15 0%, 
                  ${bot.botColor}05 50%,
                  ${bot.botColor}15 100%)`,
            opacity: isCollapsed ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
            zIndex: 0,
          },
          '&:hover': {
            transform: isImageBot && !isImagesAllowed ? 'none' : `scale(${isExpanded ? 1.01 : 1.005})`,
            boxShadow: isExpanded 
              ? `0 12px 36px -8px ${bot.botColor}60`
              : `0 8px 28px -8px ${bot.botColor}40`,
            '&:before': {
              opacity: isImageBot && !isImagesAllowed ? 0 : 1,
            }
          }
        }}
        onClick={onToggle}
      >
        {/* Upgrade Button Overlay for disabled state */}
        {isImageBot && !isImagesAllowed && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0, 0, 0, 0.65)',
              backdropFilter: 'blur(3px)',
              borderRadius: '16px',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 11,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                background: 'rgba(0, 0, 0, 0.2)',
                p: 4,
                borderRadius: '12px',
                backdropFilter: 'blur(4px)',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  mb: 1,
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                  fontWeight: 600,
                }}
              >
                Unlock AI Image Generation
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255,255,255,0.95)',
                  mb: 3,
                  fontSize: '1.1rem',
                  textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                }}
              >
                Upgrade your plan to create stunning AI-generated images
              </Typography>
              <Button
                variant="contained"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleUpgradeClick(e);
                }}
                sx={{
                  bgcolor: '#be58da',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  boxShadow: '0 0 20px rgba(190, 88, 218, 0.5)',
                  border: '2px solid rgba(190, 88, 218, 0.8)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  zIndex: 12,
                }}
              >
                Upgrade Now
              </Button>
            </Box>
          </Box>
        )}

        <CardContent
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            padding: isCollapsed ? "0" : "32px",
            "&:last-child": { pb: isCollapsed ? 0 : 4 },
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            opacity: isCollapsed ? 0 : 1,
            transform: isCollapsed ? 'scale(0.98)' : 'scale(1)',
            overflow: 'visible',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {!isCollapsed && (
            <>
              <Box 
                sx={{ 
                  display: "flex", 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 2, sm: 4 }, 
                  mb: showContent ? 3 : 0,
                  transition: 'margin 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <Box 
                  sx={{ 
                    display: "flex", 
                    flexDirection: "column", 
                    alignItems: "center",
                    alignSelf: { xs: 'center', sm: 'flex-start' },
                    width: isExpanded ? "220px" : { xs: "160px", sm: "160px" },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: isAnimating ? 'translateX(-16px)' : 'translateX(0)',
                  }}
                >
                  <Box
                    sx={{
                      width: isExpanded ? "220px" : { xs: "160px", sm: "160px" },
                      height: isExpanded ? "220px" : { xs: "160px", sm: "160px" },
                      borderRadius: "24px",
                      overflow: "hidden",
                      mb: 2,
                      border: `1px solid ${bot.botColor}40`,
                      background: `linear-gradient(135deg, ${bot.botColor}15 0%, ${bot.botColor}05 100%)`,
                      boxShadow: `0 8px 32px -8px ${bot.botColor}30`,
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      transform: showContent ? 'scale(1.05)' : 'scale(1)',
                    }}
                  >
                    <img
                      src={characterImages[bot.botId]}
                      alt={bot.botName}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: bot.botColor, 
                      fontWeight: 600, 
                      fontSize: isExpanded ? '2rem' : '1.5rem', 
                      textAlign: "center",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      wordBreak: "break-word",
                    }}
                  >
                    {bot.botName}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleChatClick}
                    disabled={isImageBot && !isImagesAllowed}
                    sx={{
                      mt: 2,
                      bgcolor: `color-mix(in srgb, ${bot.botColor} 35%, black)`,
                      color: 'white',
                      '&:hover': {
                        bgcolor: `color-mix(in srgb, ${bot.botColor} 45%, black)`,
                        transform: 'translateY(-2px)',
                        boxShadow: `
                          0 0 5px ${bot.botColor},
                          0 0 10px ${bot.botColor},
                          0 0 20px ${bot.botColor}80
                        `,
                        border: `1px solid ${bot.botColor}`,
                        '&:before': {
                          transform: 'translateX(100%)'
                        }
                      },
                      '&.Mui-disabled': {
                        backgroundColor: 'action.disabledBackground',
                        opacity: 0.5,
                      },
                      textTransform: 'none',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      px: 3,
                      py: 1.2,
                      borderRadius: '8px',
                      border: `1px solid ${bot.botColor}90`,
                      boxShadow: `
                        0 0 2px ${bot.botColor},
                        0 0 4px ${bot.botColor}40
                      `,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        background: `linear-gradient(
                          90deg,
                          transparent,
                          rgba(255, 255, 255, 0.1),
                          transparent
                        )`,
                        transition: 'transform 0.5s ease-in-out',
                        zIndex: 1
                      }
                    }}
                  >
                    {isImageBot ? (isImagesAllowed ? 'Use Expert' : 'Upgrade to Generate Images') : 'Use Expert'}
                  </Button>
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "flex-start", 
                    mb: 2,
                    flexWrap: "wrap",
                    gap: 1
                  }}>
                    <Typography
                      variant="h5"
                      sx={{
                        color: "text.primary",
                        fontWeight: 600,
                        fontSize: "1.75rem",
                        lineHeight: 1.3,
                        background: `linear-gradient(135deg, ${bot.botColor} 0%, ${bot.botColor}80 100%)`,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        wordBreak: "break-word",
                        flex: 1,
                        minWidth: 0
                      }}
                    >
                      {bot.botGroup}
                    </Typography>
                    <ChevronRight 
                      size={24} 
                      style={{ 
                        color: bot.botColor, 
                        transform: showContent ? 'rotate(90deg)' : 'none', 
                        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        flexShrink: 0
                      }} 
                    />
                  </Box>
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ 
                      mb: 2,
                      lineHeight: 1.6,
                      fontSize: '1rem',
                      wordBreak: "break-word",
                      whiteSpace: "pre-wrap"
                    }}
                  >
                    {bot.botDescription}
                  </Typography>
                  {!isExpanded && (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                      {bot.tasks.slice(0, 3).map((task, index) => (
                        <Typography
                          key={index}
                          variant="caption"
                          sx={{
                            color: bot.botColor,
                            backgroundColor: `${bot.botColor}15`,
                            padding: "6px 12px",
                            borderRadius: "8px",
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            wordBreak: "break-word"
                          }}
                        >
                          {task.taskTitle}
                        </Typography>
                      ))}
                      {bot.tasks.length > 3 && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            backgroundColor: "action.hover",
                            padding: "6px 12px",
                            borderRadius: "8px",
                            fontWeight: 500,
                            fontSize: '0.875rem',
                          }}
                        >
                          +{bot.tasks.length - 3} more
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>

              {isExpanded && (
                <Box 
                  sx={{ 
                    mt: 3,
                    position: 'relative',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                      gap: 2,
                      opacity: showContent ? 1 : 0,
                      transform: showContent ? 'translateY(0)' : 'translateY(20px)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {bot.tasks.map((task, index) => (
                      <TaskCard
                        key={index}
                        task={task}
                        backgroundColor={bot.botColor}
                        searchTerms={searchTerms}
                        isExpanded={isExpanded}
                        bot={bot}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <SubscriptionModal
        open={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        subscriptionUrl={subscriptionUrl}
      />
    </>
  );
};

const Dashboard = () => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [expandedBotIndex, setExpandedBotIndex] = React.useState(null);
  const [showSubheading, setShowSubheading] = React.useState(false);

  const searchTerms = React.useMemo(() => 
    searchQuery.toLowerCase().trim().split(/\s+/).filter(Boolean),
    [searchQuery]
  );

  const filteredBots = React.useMemo(() => {
    if (!searchQuery.trim()) return bots;
    
    return bots.filter(bot => {
      // Search in bot group and description
      const botGroupMatch = bot.botGroup.toLowerCase().includes(searchQuery.toLowerCase());
      const botDescriptionMatch = bot.botDescription.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Search in tasks
      const hasMatchingTask = bot.tasks.some(task => {
        const searchableText = [
          task.taskTitle.toLowerCase(),
          task.taskDescription.toLowerCase(),
          ...(task.keywords || []).map(k => k.toLowerCase())
        ].join(' ');
        
        return searchTerms.some(term => searchableText.includes(term));
      });

      return botGroupMatch || botDescriptionMatch || hasMatchingTask;
    });
  }, [searchQuery, searchTerms]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    // Collapse all cards when search is cleared
    if (!e.target.value.trim()) {
      setExpandedBotIndex(null);
    }
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        py: 4,
        px: { xs: 2, sm: 2, md: 4 },
        maxWidth: "1800px",
        margin: "0 auto",
        overflow: "hidden",
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          mb: 12,
          mt: 6,
        }}
      >
        <img
          src={mainLogo}
          alt="SpeareAI Logo"
          style={{
            width: '300px',
            height: 'auto',
            marginBottom: '2rem',
            marginLeft: '-6px',
          }}
        />
        <Typography 
          variant="h3" 
          component="h1" 
          sx={{ 
            fontWeight: 700,
            mb: 3,
            minHeight: '3.5rem',
          }}
        >
          <TypeWriter 
            text="Your Personal AI Workforce" 
            delay={40}
            onComplete={() => setShowSubheading(true)}
          />
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary"
          sx={{ 
            mb: 6,
            maxWidth: '800px',
            fontSize: '1.25rem',
            lineHeight: 1.4,
            minHeight: '3rem',
            opacity: showSubheading ? 1 : 0,
            transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {showSubheading && (
            <TypeWriter 
              text="Instantly access expert AI specialists for any business task — from content and marketing to strategy and documentation"
              delay={20}
            />
          )}
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          width: '100%',
          maxWidth: '800px',
        }}>
          <TextField
            fullWidth
            placeholder="What task do you need help with?"
            value={searchQuery}
            onChange={handleSearch}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                height: '72px',
                backgroundColor: 'background.paper',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '& fieldset': {
                  borderWidth: '2px',
                  borderColor: '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: '#be58da',
                  borderWidth: '2px',
                },
                '&.Mui-focused': {
                  '& fieldset': {
                    borderColor: '#be58da',
                    borderWidth: '2px',
                  },
                  boxShadow: `
                    0 0 5px rgba(190, 88, 218, 0.3),
                    0 0 10px rgba(190, 88, 218, 0.2),
                    0 0 20px rgba(190, 88, 218, 0.1)
                  `,
                },
                '& input': {
                  fontSize: '1.1rem',
                  padding: '0.75rem 1rem 0.75rem 0.5rem',
                  '&::placeholder': {
                    opacity: 0.7,
                  },
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    ml: 1.5,
                    mr: 1,
                  }}>
                    <Search 
                      size={28} 
                      style={{ 
                        color: '#be58da',
                        strokeWidth: 2.5
                      }} 
                    />
                  </Box>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      <Typography 
        variant="h4" 
        component="h2"
        sx={{
          mb: 6,
          textAlign: 'center',
          fontWeight: 600,
          fontSize: '2.5rem',
          position: 'relative',
          display: 'inline-block',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(190, 88, 218, 0.1), transparent)',
            transition: 'transform 0.5s ease-in-out',
            transform: 'translateX(-100%)',
          },
          '&:hover': {
            '&:before': {
              transform: 'translateX(200%)',
            }
          }
        }}
      >
        {filteredBots.length === 0 ? (
          "No matching specialists found"
        ) : (
          `Your AI Team${searchQuery ? ` (${filteredBots.length} specialists found)` : ''}`
        )}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'repeat(12, 1fr)'
          },
          gap: 3,
          position: 'relative',
        }}
      >
        {filteredBots.map((bot, index) => {
          const isExpanded = expandedBotIndex === index;
          const isCollapsed = (index % 2 === 0 && expandedBotIndex === index + 1) || 
                            (index % 2 === 1 && expandedBotIndex === index - 1);

          return (
            <Box
              key={index}
              sx={{
                gridColumn: {
                  xs: '1 / -1',
                  lg: isExpanded ? 'span 11' : isCollapsed ? 'span 1' : 'span 6'
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <BotCard 
                bot={bot}
                isExpanded={isExpanded}
                isCollapsed={isCollapsed}
                isSecondInRow={index % 2 === 1}
                onToggle={() => setExpandedBotIndex(expandedBotIndex === index ? null : index)}
                searchTerms={searchTerms}
              />
            </Box>
          );
        })}
      </Box>
    </Container>
  );
};

export default Dashboard;
